/*
* Copyright (c) 2026 Send2Me, media capture and delivery platform. All rights reserved.
* Distributed under the TruVideo proprietary license.
*/

/** Standard ServiceHub email/username + password login. */
export const DEFAULT_LOGIN_ROUTE = {
  authPath: '/auth/login',
  showForgotPassword: true,
  isExternal: false,
};

/** Pixo login, used by /login/:accountSlug when the account has Pixo enabled. */
export const PIXO_LOGIN_ROUTE = {
  authPath: '/auth/pixo',
  showForgotPassword: false,
  isExternal: true,
};

/**
 * Frontend URLs whose login page authenticates through a different backend endpoint.
 * - `url`: frontend URL. Matched on hostname (case-insensitive) and, only when the URL
 *   specifies one, port — protocol and path are ignored, so any page on that host matches.
 * - `authPath`: backend endpoint (relative to /api) that receives `{ username, password }`
 *   and returns ServiceHub tokens under `serviceHub` (see POST /api/auth/:service).
 * - `showForgotPassword`: whether the login page shows the "Forgot Password?" link.
 */
export const LOGIN_ROUTE_MAPPINGS = [
  //PIXO+LOCAL-DEVELOPMENT
  {
    url: 'http://pixo.truvideo-servicehub.localhost/',
    authPath: '/auth/pixo',
    showForgotPassword: false,
  },
  //PIXO+SERVICEHUB-DEV
  {
    url: '<needs to configure dev url here>',
    authPath: '/auth/pixo',
    showForgotPassword: false,
  },
  //PIXO+SERVICEHUB-PROD
  {
    url: '<needs to configure prod url here>',
    authPath: '/auth/pixo',
    showForgotPassword: false,
  },
];

const matchesLocation = (mappingUrl, location) => {
  try {
    const { hostname, port } = new URL(mappingUrl);
    return (
      hostname.toLowerCase() === location.hostname.toLowerCase() &&
      (!port || port === location.port)
    );
  } catch {
    console.warn(`[loginRoutes] Invalid mapping URL "${mappingUrl}"`);
    return false;
  }
};

/** Returns the login route for the current (or given) location; falls back to the default. */
export const resolveLoginRoute = (location = window.location) => {
  const mapping = LOGIN_ROUTE_MAPPINGS.find((m) => matchesLocation(m.url, location));
  return mapping
    ? { authPath: mapping.authPath, showForgotPassword: mapping.showForgotPassword, isExternal: true }
    : DEFAULT_LOGIN_ROUTE;
};

/** Backend auth paths of all mappings — failed calls to these must not trigger a token refresh. */
export const EXTERNAL_AUTH_PATHS = [
  ...new Set([...LOGIN_ROUTE_MAPPINGS.map((m) => m.authPath), PIXO_LOGIN_ROUTE.authPath]),
];

// ── Account login page (/login/:accountSlug) ──────────────────────────────────

/** Backend endpoint of the account login page; the server picks Pixo or local sign-in. */
export const ACCOUNT_LOGIN_PATH_PREFIX = '/auth/accounts/login/';
export const accountLoginPath = (slug) => `${ACCOUNT_LOGIN_PATH_PREFIX}${encodeURIComponent(slug)}`;

const ACCOUNT_SLUG_KEY = 'login_account_slug';
const ACCOUNT_SLUG_PATTERN = /^[a-z0-9-]+$/;

/** Normalized account slug, or null when the value isn't a valid slug. */
export const normalizeAccountSlug = (slug) => {
  const value = typeof slug === 'string' ? slug.trim().toLowerCase() : '';
  return ACCOUNT_SLUG_PATTERN.test(value) ? value : null;
};

/** Remembers which account login page the user signed in from (null forgets it). */
export const setLoginAccountSlug = (slug) => {
  try {
    const value = normalizeAccountSlug(slug);
    if (value) localStorage.setItem(ACCOUNT_SLUG_KEY, value);
    else localStorage.removeItem(ACCOUNT_SLUG_KEY);
  } catch {
    // storage unavailable — fall back to the default login page
  }
};

/** Account slug of the login page the user signed in from, or null. */
export const getLoginAccountSlug = () => {
  try {
    return normalizeAccountSlug(localStorage.getItem(ACCOUNT_SLUG_KEY));
  } catch {
    return null;
  }
};

/** Login page to send the user to: the account login page they used, or /login. */
export const getLoginPath = () => {
  const slug = getLoginAccountSlug();
  return slug ? `/login/${encodeURIComponent(slug)}` : '/login';
};
